import React from 'react';
import { BarChart2Icon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, MinusSquareIcon, DollarSignIcon, FileTextIcon, PlusIcon, ClockIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
export function CreditNoteDashboard({
  creditNotes = [],
  setCurrentView,
  locale = 'en-US'
}) {
  const defaultCurrency = getDefaultCurrency();
  // Calculate summary statistics
  const totalCreditNotes = creditNotes.length;
  const totalAmount = creditNotes.reduce((sum, cn) => sum + cn.amount, 0);
  const appliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.appliedAmount || 0), 0);
  const unappliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.unappliedAmount || 0), 0);
  // Count by status
  const statusCounts = creditNotes.reduce((counts, cn) => {
    counts[cn.status] = (counts[cn.status] || 0) + 1;
    return counts;
  }, {});
  // Recent credit notes (last 5)
  const recentCreditNotes = [...creditNotes].sort((a, b) => new Date(b.creditNoteDate) - new Date(a.creditNoteDate)).slice(0, 5);
  return <div data-id="element-3273">
      <div className="mb-8 flex justify-between items-center" data-id="element-3274">
        <div data-id="element-3275">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3276">
            Credit Notes Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3277">
            Overview of credit note status and activity
          </p>
        </div>
        <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3278">
          <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-3279" />
          New Credit Note
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-3280">
        {/* Total Credit Notes */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3281">
          <div className="px-4 py-5 sm:p-6" data-id="element-3282">
            <div className="flex items-center" data-id="element-3283">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-3284">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-3285" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3286">
                <dl data-id="element-3287">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3288">
                    Total Credit Notes
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3289">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3290">
                      {totalCreditNotes}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3291">
            <div className="text-sm" data-id="element-3292">
              <a href="#" onClick={() => setCurrentView('credit-note-list')} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3293">
                View all credit notes
              </a>
            </div>
          </div>
        </div>
        {/* Total Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3294">
          <div className="px-4 py-5 sm:p-6" data-id="element-3295">
            <div className="flex items-center" data-id="element-3296">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-3297">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-3298" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3299">
                <dl data-id="element-3300">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3301">
                    Total Credit Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3302">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3303">
                      {formatCurrency(totalAmount, defaultCurrency, locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3304">
            <div className="text-sm" data-id="element-3305">
              <span className="font-medium text-gray-500" data-id="element-3306">
                Applied:{' '}
                {formatCurrency(appliedAmount, defaultCurrency, locale)}
              </span>
            </div>
          </div>
        </div>
        {/* Unapplied Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3307">
          <div className="px-4 py-5 sm:p-6" data-id="element-3308">
            <div className="flex items-center" data-id="element-3309">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-3310">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-3311" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3312">
                <dl data-id="element-3313">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3314">
                    Unapplied Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3315">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3316">
                      {formatCurrency(unappliedAmount, defaultCurrency, locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3317">
            <div className="text-sm" data-id="element-3318">
              <a href="#" className="font-medium text-yellow-600 hover:text-yellow-500" data-id="element-3319">
                {unappliedAmount > 0 ? 'Needs attention' : 'All credits applied'}
              </a>
            </div>
          </div>
        </div>
        {/* Pending Approval */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3320">
          <div className="px-4 py-5 sm:p-6" data-id="element-3321">
            <div className="flex items-center" data-id="element-3322">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-3323">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-3324" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3325">
                <dl data-id="element-3326">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3327">
                    Pending Approval
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3328">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3329">
                      {statusCounts['Pending'] || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3330">
            <div className="text-sm" data-id="element-3331">
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-3332">
                {statusCounts['Pending'] ? 'Review pending credit notes' : 'No pending approvals'}
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-3333">
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3334">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3335">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3336">
              Credit Notes by Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3337">
            <div className="space-y-4" data-id="element-3338">
              <div className="flex items-center justify-between" data-id="element-3339">
                <div className="flex items-center" data-id="element-3340">
                  <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-3341"></div>
                  <span className="text-sm text-gray-500" data-id="element-3342">Draft</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3343">
                  {statusCounts['Draft'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3344">
                <div className="flex items-center" data-id="element-3345">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-3346"></div>
                  <span className="text-sm text-gray-500" data-id="element-3347">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3348">
                  {statusCounts['Pending'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3349">
                <div className="flex items-center" data-id="element-3350">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-3351"></div>
                  <span className="text-sm text-gray-500" data-id="element-3352">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3353">
                  {statusCounts['Approved'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3354">
                <div className="flex items-center" data-id="element-3355">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-3356"></div>
                  <span className="text-sm text-gray-500" data-id="element-3357">Posted</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3358">
                  {statusCounts['Posted'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3359">
                <div className="flex items-center" data-id="element-3360">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-3361"></div>
                  <span className="text-sm text-gray-500" data-id="element-3362">Rejected</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3363">
                  {statusCounts['Rejected'] || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3364">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3365">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3366">
              Application Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3367">
            <div className="flex items-center justify-between mb-2" data-id="element-3368">
              <span className="text-sm font-medium text-gray-500" data-id="element-3369">
                Total Credit Amount
              </span>
              <span className="text-sm font-medium text-gray-900" data-id="element-3370">
                {formatCurrency(totalAmount, defaultCurrency, locale)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-3371">
              <div className="bg-green-600 h-2.5 rounded-full" style={{
              width: `${appliedAmount / totalAmount * 100}%`
            }} data-id="element-3372"></div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3373">
              <div className="bg-green-50 p-4 rounded-md" data-id="element-3374">
                <div className="flex items-center" data-id="element-3375">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3376" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3377">
                    Applied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3378">
                  {formatCurrency(appliedAmount, defaultCurrency, locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3379">
                  {Math.round(appliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-md" data-id="element-3380">
                <div className="flex items-center" data-id="element-3381">
                  <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-2" data-id="element-3382" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3383">
                    Unapplied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3384">
                  {formatCurrency(unappliedAmount, defaultCurrency, locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3385">
                  {Math.round(unappliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Credit Notes */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3386">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3387">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3388">
            Recent Credit Notes
          </h3>
          <a href="#" onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3389">
            View all
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-3390">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-3391">
            <thead className="bg-gray-50" data-id="element-3392">
              <tr data-id="element-3393">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3394">
                  Credit Note #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3395">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3396">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3397">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3398">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-3399">
                  <span className="sr-only" data-id="element-3400">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3401">
              {recentCreditNotes.length > 0 ? recentCreditNotes.map(creditNote => <tr key={creditNote.id} className="hover:bg-gray-50" data-id="element-3402">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-3403">
                      {creditNote.creditNoteNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3404">
                      {creditNote.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3405">
                      {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3406">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3407">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${creditNote.status === 'Approved' ? 'bg-green-100 text-green-800' : creditNote.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : creditNote.status === 'Draft' ? 'bg-gray-100 text-gray-800' : creditNote.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-3408">
                        {creditNote.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3409">
                      <a href="#" onClick={() => {
                  setCurrentView('credit-note-details');
                }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3410">
                        View
                      </a>
                    </td>
                  </tr>) : <tr data-id="element-3411">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3412">
                    No credit notes found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}